/** The Salary tab's pay periods: the person's live payroll runs and the detail a pressed period opens. Decisions live here because vitest cannot render a component.
 *  ⛔ Nothing here computes pay: an issued run's figures and `rate_breakdown` are frozen at issue; a draft is served with no figures, so it is said to be a draft rather than shown as a number. */

export interface SalaryRunPeriod {
  id: string
  year: number
  month: number
  run: 'advance' | 'final' | string
  status: 'Issued' | 'Draft' | string
  cut_off: string | null
  issued_at: string | null
  rate_breakdown: Record<string, unknown>[] | null
  total_hours: string | null
  shift_count: string | null
  included_days: number | null
  al_days: string | null
  allowance_value: string | null
  gross: string | null
  bonus: string | null
  deduction: string | null
  social: string | null
  tax: string | null
  net: string | null
  advance_paid: string | null
  advance_adjustment: string | null
  net_adjustment: string | null
  payable: string | null
  /** False on an imported final whose source never recorded its social, tax or net (`salary_run.figures_recorded`). Absent reads as recorded. */
  figures_recorded?: boolean
}

/** What the detail can honestly show: `frozen` (issued, with its frozen working); `totals` (issued before the breakdown was frozen, totals only); `draft` (nothing frozen, no figure stated). */
type PeriodDetailKind = 'frozen' | 'totals' | 'draft'

export function periodDetailKind(run: Pick<SalaryRunPeriod, 'status' | 'rate_breakdown'>): PeriodDetailKind {
  if (run.status !== 'Issued') return 'draft'
  return Array.isArray(run.rate_breakdown) ? 'frozen' : 'totals'
}

/** `YYYY-MM`: a payroll month, not a calendar month. */
export function periodLabel(run: Pick<SalaryRunPeriod, 'year' | 'month'>): string {
  return `${run.year}-${String(run.month).padStart(2, '0')}`
}

interface SegmentLine {
  basis: string
  rate: number
  start: string
  end: string
  hours: number
  shifts: number
  allowance: number
  tasks: number
  taskPay: number
  gross: number
  carried: boolean
}

const num = (value: unknown): number => {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? n : 0
}

/** An imported advance's days-ledger segment (no rate, span or basis): not a rate row, or it would print 0s against a real gross. Same test as `isLedgerOnly`. */
function isLedgerOnlyRaw(s: Record<string, unknown>): boolean {
  return (s.rate ?? s.Rate) == null && (s.type ?? s.model ?? s.Type) == null && (s.start ?? s.Start) == null && (s.end ?? s.End) == null
}

/** The days a ledger-only segment paid for; `null` when none is recorded. */
export function ledgerSegmentDays(breakdown: readonly Record<string, unknown>[] | null): string[] | null {
  const segs = (breakdown ?? []).filter(isLedgerOnlyRaw)
  if (segs.length === 0) return null
  return segs.flatMap((s) => (Array.isArray(s.days) ? s.days.map(String) : []))
}

/** The frozen segments as rows: one rate in force over one span of days. Two shapes: a run issued here carries `services/payroll.frozen_breakdown`'s lowercase keys; an IMPORTED run carries the legacy `T-SalaryCal` keys (`Type`, `Rate`, `Start`, `End`, `TotalHours`, `TotalShift`, `AddValue`, `Task`, `TaskPay`, `Gross`). A key neither carries reads 0. */
export function segmentLines(breakdown: readonly Record<string, unknown>[] | null): SegmentLine[] {
  return (breakdown ?? []).filter((s) => !isLedgerOnlyRaw(s)).map((s) => ({
    basis: String(s.type ?? s.model ?? s.Type ?? ''),
    rate: num(s.rate ?? s.Rate),
    start: String(s.start ?? s.Start ?? ''),
    end: String(s.end ?? s.End ?? ''),
    hours: num(s.hours ?? s.TotalHours),
    shifts: num(s.shifts ?? s.TotalShift),
    allowance: num(s.allowance ?? s.AddValue),
    tasks: num(s.tasks ?? s.Task),
    taskPay: num(s.task_pay ?? s.TaskPay),
    gross: num(s.gross ?? s.Gross),
    carried: !!s.carry_over,
  }))
}

/** Whether an issued run's deductions and payable were RECORDED. The importer wrote a blank `Net`/`Remainder`/`Social`/`Tax` as 0, so a totals-only imported run carries its gross and zeros for the rest; those zeros are UNKNOWN, and an unknown is said, never shown as a figure. A draft states no figure anyway. */
export function figuresRecorded(
  run: Pick<SalaryRunPeriod, 'status' | 'rate_breakdown' | 'figures_recorded'>,
): boolean {
  if (periodDetailKind(run) !== 'totals') return true
  return !netNotRecorded(run)
}

/** The STORED fact (`salary_run.figures_recorded`), not the shape: a gross above 0 paying a net of 0 cannot tell an unrecorded run from a real 0. Absent (an older response) reads as recorded. */
export function netNotRecorded(run: { figures_recorded?: boolean }): boolean {
  return run.figures_recorded === false
}

/** The money lines of an issued run, in payslip order; a zero adjustment line is dropped, a zero figure line kept. `null` on a draft. A line whose `value` is `null` was NOT RECORDED (`figuresRecorded`): social, tax, net, the advance and the payable; the adjustments are dropped. */
export function moneyLines(
  run: SalaryRunPeriod,
): { key: string; value: number | null }[] | null {
  if (run.status !== 'Issued') return null
  const always = ['gross', 'allowance_value', 'bonus', 'deduction', 'social', 'tax', 'net'] as const
  if (!figuresRecorded(run)) {
    const unknown = new Set<string>(['social', 'tax', 'net'])
    return [
      ...always.map((key) => ({ key, value: unknown.has(key) ? null : num(run[key]) })),
      { key: 'advance_paid', value: null },
      { key: 'payable', value: null },
    ]
  }
  const optional = ['advance_paid', 'advance_adjustment', 'net_adjustment'] as const
  const lines: { key: string; value: number | null }[] = always.map((key) => ({ key, value: num(run[key]) }))
  for (const key of optional) {
    const value = num(run[key])
    if (value !== 0) lines.push({ key, value })
  }
  lines.push({ key: 'payable', value: num(run.payable) })
  return lines
}
